/**
 * Import React libraries.
 */
import * as React from 'react';

/**
 * Import custom components.
 */
import {Size} from '../types'

/**
 * Import custom styles.
 */
import {StyledTable, StyledTableSpinner, TableWrapper} from './TableStyles';

/**
 * Import custom types.
 */
import {TableProps, TableColumn, TableSortingDirection, TableSortProps} from './TableTypes';
import {LoadingIndicator} from '../LoadingIndicator';
import TableHeader from './TableHeaders';
import TableFooter from './TableFooter';
import TableBody from './TableBody';

const Table: React.FunctionComponent<TableProps> = (props: TableProps) => {
  let {
    onPreviousPageClick,
    onNextPageClick,
    onRowsPerPageChange,
    onTriggerSortingChange,
    columns,
    rows,
    remoteOperations,
    pagination,
    showLoadingIndicator,
    selectable,
    multiSelect,
    keyExpr,
    sortProps,
    accordion,
    collapsed = true,

    collapsedRows,
    rowsPerPageLabel,
    loaderZIndex
  } = props;

  // States used within the component
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [filteredRows, setFilteredRows] = React.useState<any[]>([]);
  const [currentPageRows, setCurrentPageRows] = React.useState<any[]>([]);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [sort, setSort] = React.useState<TableSortProps | undefined>();
  const [from, setFrom] = React.useState<number>();
  const [to, setTo] = React.useState<number>();
  const [total, setTotal] = React.useState<number>();

  const [isCollapsed, setIsCollapsed] = React.useState(true);

  const [selectAllState, setSelectAllState] = React.useState<'all' | 'some' | 'none'>('none');

  const [selected, setSelected] = React.useState<any | any[]>(multiSelect ? [] : undefined);

  React.useEffect(() => {
    setSort({
      column: sortProps?.column ?? '', 
      direction: sortProps?.direction ?? 'asc', 
      allowClearingSort: sortProps?.allowClearingSort === undefined ? true : sortProps.allowClearingSort
    });
  }, [sortProps])

  React.useEffect(() => {
    setSelected(props.selectedRows || (multiSelect ? [] : undefined))
  }, [props.selectedRows, multiSelect])

  /**
   * update select all checkbox state
   */
  React.useEffect(() => {
    if (multiSelect) {
      const currentList = currentPageRows.map(row => keyExpr ? row[keyExpr] : row);
      const count = selected.filter((item: any) => currentList.includes(item)).length;

      setSelectAllState(count === currentList.length ? 'all' : count > 0 ? 'some' : 'none');
    }
  }, [currentPageRows, multiSelect, selected, keyExpr])

  /**
   * Takes care of remote pagination.
   * Works only in case remote pagination is set to true.
   */
  React.useEffect(() => {
    if (remoteOperations) {
      setCurrentPageRows(rows);

      // Let's assign pagination values
      setFrom(pagination?.from);
      setTo(pagination?.to);
      setTotal(pagination?.total);
      setCurrentPage(pagination?.currentPage!);
      setRowsPerPage(pagination?.rowsPerPage!);
    }
  }, [remoteOperations, pagination, rows]);

  /**
   * Sets number of rows to display when accordion.
   */
  React.useEffect(() => {
    if (!accordion) return;

    setRowsPerPage(isCollapsed ? collapsedRows || 10 : rows.length);
  }, [rows, accordion, collapsedRows, isCollapsed])

  React.useEffect(() => {
    if (!accordion) return;

    setIsCollapsed(!!collapsed);
  }, [accordion, collapsed])

  /**
   * Filters out a rows by specific column filters and sorts them if any sorting is set.
   * @returns Filtered and sorted rows.
   */
  const filterAndSortRows = (): any[] => {
    let tmpFilteredRows = rows.filter((row) => {
      // Let's start with matched filter criteria
      let matchFilterCriteria = true;

      // Let's go through columns
      for (const column of columns) {
        // Let's check if filter value is specified for the column
        // And if it doesn't match the current row column value
        // Then let's update match filter criteria to failed
        if (column.filterValue && row[column.key].toLowerCase().indexOf(column.filterValue.toLowerCase()) === -1) {
          matchFilterCriteria = false;
        }
      }

      // Let's return row only in case match filter criteria succeeds
      if (matchFilterCriteria) {
        return row;
      }
    });

    // Let's apply sorting if needed
    if (!!sort?.column && !!sort?.direction) {
      // Let's sort the rows
      tmpFilteredRows.sort((a, b) => {
        if (a[sort.column!] > b[sort.column!]) {
          return sort.direction! === 'asc' ? 1 : -1;
        } else if (a[sort.column!] < b[sort.column!]) {
          return sort.direction! === 'asc' ? -1 : 1;
        } else {
          return 0;
        }
      });
    }

    // Let's return filtered rows
    return tmpFilteredRows;
  };

  /**
   * Takes care of local pagination.
   * Works only in case remote pagination is not defined or is set to false.
   */
  React.useEffect(() => {
    if (!remoteOperations) {
      // Let's retrieve temporary filtered rows and update globally filtered rows
      const tmpFilteredRows = filterAndSortRows();
      setFilteredRows(tmpFilteredRows);
      setTotal(tmpFilteredRows.length);

      // Assign rows from and rows to
      const rowsFrom = (currentPage - 1) * rowsPerPage;
      const rowsTo = rowsFrom + rowsPerPage >= tmpFilteredRows.length ? tmpFilteredRows.length : rowsFrom + rowsPerPage;

      // Set from and to values
      setFrom(rowsFrom + 1);
      setTo(rowsTo);

      // Filter out the rows
      setCurrentPageRows(tmpFilteredRows.slice(rowsFrom, rowsTo));
    }
  }, [rowsPerPage, rows, currentPage, columns, sort, remoteOperations]);


  /**
   * Applies a specific sorting to a column.
   * If no sorting exists, then applies ascending initially
   * @param column - Column to which sorting should be applied.
   */
  const sortTableColumn = (column: TableColumn): void => {
    let columnKey = '';
    let sortDirection: TableSortingDirection | undefined = undefined;

    if (sort?.column !== column.key) {
      columnKey = column.key;
      sortDirection = 'asc';
    } else {
      if (sort?.direction === 'desc' && sort?.allowClearingSort) {
        columnKey = '';
        sortDirection = undefined;
      } else {
        columnKey = sort.column;
        sortDirection = sort.direction === 'asc' ? 'desc' : 'asc';
      }
    }

    if(columnKey == '')
      setSort({ column: '', direction: 'asc', allowClearingSort: sort?.allowClearingSort});
    else
      setSort({column: columnKey, direction: sortDirection!, allowClearingSort: sort?.allowClearingSort});

    remoteOperations && onTriggerSortingChange && onTriggerSortingChange(columnKey, sortDirection);
  };

  /**
   * Navigates user back to the previous page and updates the current table page.
   */
  const previousPage = () => {
    if (remoteOperations) {
      // Let's inform parent component about page change
      if (onPreviousPageClick) {
        onPreviousPageClick();
      }
    } else {
      // Let's change the page within the component
      setCurrentPage(currentPage > 1 ? currentPage - 1 : 1);
    }
  };

  /**
   * Navigates user to the next page and updates the current table page.
   */
  const nextPage = () => {
    if (remoteOperations) {
      // Let's inform parent component about page change
      if (onNextPageClick) {
        onNextPageClick();
      }
    } else {
      // Let's change the page within the component
      setCurrentPage(currentPage * rowsPerPage >= filteredRows.length ? currentPage : currentPage + 1);
    }
  };

  /**
   * Updates the rows per page value and refreshes the table content accordingly.
   * @param value - Newly selected rows per page value.
   */
  const changeRowsPerPage = (value: number) => {
    // Let's check if we are using remote pagination
    if (remoteOperations) {
      // Inform parent component about rows per page change
      if (onRowsPerPageChange) {
        onRowsPerPageChange(value);
      }
    } else {
      // Let's reset current page to 1
      setCurrentPage(1);
    }

    // Let's set rows per page
    setRowsPerPage(value);
  };


  /**
   * Function which is called when a mouse click happens on a row to pass data to the parent component.
   * @param row - Row in which the link is located.
   */
  const onRowClick = (row: any): void => {
    const selectedText = window?.getSelection()?.toString();

    if (selectedText?.length === 0 && selectable) {
      const value = keyExpr ? row[keyExpr] : row;
      let currentSelection = value;
      if (!!multiSelect) {
        const existing = selected?.indexOf(value);
        if (existing > -1) {
          currentSelection = [...selected];
          currentSelection.splice(existing, 1);
        } else {
          currentSelection = [...selected, currentSelection];
        }
      }

      setSelected(currentSelection)
      props.onSelectionChange && props.onSelectionChange(currentSelection);
    }
  };

  /**
   * Function which is called when user clicks checkbox to select or deselect all rows.
   */
  const onSelectAllClick = () => {
    const currentList = currentPageRows.map(row => keyExpr ? row[keyExpr] : row);

    switch (selectAllState) {
      case 'all':
        setSelected([...selected].filter(item => !currentList.includes(item)));
        setSelectAllState('none');
        break;
      case 'none':
        setSelected([...selected, ...currentList]);
        setSelectAllState('all');
        break;
      case 'some':
        setSelected([...selected, ...currentList.filter(item => !selected.includes(item))]);
        setSelectAllState('all');
        break;
    }
  };

  /**
   * Return Table component.
   */
  return (
    <TableWrapper className={props.border ? 'border' : undefined}>
      <TableHeader {...props}/>
      <TableBody {...props}
                 currentPageRows={currentPageRows}
                 selected={selected}
                 onRowClick={onRowClick}
                 sortByColumn={sortTableColumn}
                 onSelectAllClick={onSelectAllClick}
                 sortProps={sort}
                 selectAllState={selectAllState}/>
      <TableFooter {...props}
                   onRowsPerPageChange={changeRowsPerPage}
                   rowsPerPage={rowsPerPage}
                   setIsCollapsed={setIsCollapsed}
                   isCollapsed={isCollapsed}
                   from={from}
                   to={to}
                   total={total}
                   nextPage={nextPage}
                   prevPage={previousPage}/>
      {showLoadingIndicator && (
        <StyledTableSpinner zindex={loaderZIndex}>
          <LoadingIndicator size={Size.Medium}/>
        </StyledTableSpinner>
      )}
    </TableWrapper>
  );
};

export default Table;
